export { colors, gradients } from './colors';
export type { ColorKey, GradientKey } from './colors';

export { typographyTokens } from './typographyTokens';
export { fonts, typography } from './typography';
export type { TypographyKey, FontKey } from './typography';

export { layout, containerStyle } from './layout';
export type { LayoutKey } from './layout';

/**
 * 4-base spacing scale. Source: design-system colors_and_type.css.
 * Use these tokens — don't write hardcoded paddings/margins on new
 * components.
 */
export const spacing = {
    xs: 4,
    sm: 8,
    md: 12,
    base: 16,
    lg: 24,
    xl: 32,
    '2xl': 48,
    '3xl': 64,
    '4xl': 96,
} as const;

/**
 * Radii cluster — they don't ladder linearly. Small UI lives at 8,
 * inputs at 12, cards at 20+. CTAs are pills (100). There is no 4px
 * body radius.
 */
export const radius = {
    xs: 4,
    /** Buttons. */
    sm: 8,
    /** Inputs. */
    md: 12,
    /** Small cards. */
    lg: 16,
    /** Standard card (EventCard). */
    card: 20,
    /** Large cards, banners. */
    xl: 24,
    '2xl': 32,
    /** Hero / luxury card — Card.tsx default. */
    huge: 40,
    /** CTAs, pills, badges. */
    pill: 100,
    full: 9999,
} as const;

/**
 * Warm-tinted shadows — mixed with brown (107, 94, 85), not pure
 * black. They feel sunlit, not gray.
 *
 * These are CSS strings used by Platform.OS === 'web' style props.
 * On native, components define shadow* / elevation props directly.
 */
export const shadows = {
    subtle: '0 1px 4px rgba(0, 0, 0, 0.03)',
    card: '0 4px 20px rgba(0, 18, 29, 0.06)',
    cardHover: '0 8px 30px rgba(0, 18, 29, 0.10)',
    /** Elevated card — for master cards, featured elements. */
    cardElevated: '0 20px 60px rgba(107, 94, 85, 0.08)',
    /** Hero card — Card.tsx default. */
    luxury: '0 40px 80px -20px rgba(107, 94, 85, 0.08)',
    glass: '0 8px 32px rgba(0, 18, 29, 0.04)',
    cta: '0 10px 30px -5px rgba(233, 130, 69, 0.40)',
    ctaSoft: '0 6px 24px rgba(237, 116, 61, 0.36)',
    orangeGlow: '0 0 8px rgba(255, 112, 32, 0.15)',
} as const;

/**
 * Motion tokens — easings + durations.
 * Buttons feel languid (long 0.5s ease-out), not snappy.
 */
export const motion = {
    easeOut:    'cubic-bezier(0.16, 1, 0.3, 1)',     // primary button
    easeSpring: 'cubic-bezier(0.2, 0.8, 0.2, 1)',    // card lifts
    easeSmooth: 'cubic-bezier(0.4, 0, 0.2, 1)',      // nav, hover
    durFast:    '0.2s',
    durBase:    '0.3s',
    durSlow:    '0.5s',
} as const;

export const zIndex = {
    base: 0,
    dropdown: 10,
    sticky: 20,
    fixed: 30,
    overlay: 40,
    modal: 50,
    toast: 60,
} as const;

export type SpacingKey = keyof typeof spacing;
export type RadiusKey = keyof typeof radius;
export type ShadowKey = keyof typeof shadows;
export type MotionKey = keyof typeof motion;
export type ZIndexKey = keyof typeof zIndex;
